import type { Theme } from "@mui/material";
import {
  Dialog,
  DialogContent,
  FormHelperText,
  useMediaQuery,
} from "@mui/material";
import { DIALOG_WIDTH } from "../../constants";
import StandardDialogTitle from "./StandardDialogTitle";
import * as React from "react";
import { CloseDialogHandler } from "../../contexts/DialogContext";
import { useIntegration } from "../../hooks/query/useIntegration";
import { useAlertProviders } from "../../hooks/query/useAlertProviders";
import { integrationSetupForm } from "../integrationSetupForm";
import { Integration, SystemAlertSchema } from "../../types";
import { useUpdateIntegration } from "../../hooks/mutation/update/useUpdateIntegration";
import { ApiKeyForm } from "../integrationSetupForm/ApiKeyForm";

const normalizeProviderText = (value: string | undefined): string => {
  if (!value) {
    return "";
  }

  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
};

const providerNameHints: Array<{ hint: string; key: SystemAlertSchema }> = [
  { hint: "sentry", key: "system-sentry" },
  { hint: "datadog", key: "system-datadog" },
  { hint: "coralogix", key: "system-coralogix" },
  { hint: "pagerduty", key: "system-pagerduty" },
  { hint: "opsgenie", key: "system-opsgenie" },
  { hint: "prometheus alertmanager", key: "system-prometheus-alertmanager" },
  { hint: "grafana", key: "system-grafana" },
  { hint: "splunk", key: "system-splunk" },
  { hint: "cloudwatch", key: "system-cloudwatch" },
  { hint: "azure monitor", key: "system-azure-monitor" },
  { hint: "gcp monitoring", key: "system-gcp-monitoring" },
  { hint: "google cloud monitoring", key: "system-gcp-monitoring" },
  { hint: "new relic", key: "system-newrelic" },
];

const resolveSystemSchemaByText = (
  value: string | undefined,
): SystemAlertSchema | undefined => {
  const normalized = normalizeProviderText(value);
  if (!normalized) {
    return undefined;
  }

  const exactKey = providerNameHints.find((entry) => entry.key === normalized);
  if (exactKey) {
    return exactKey.key;
  }

  const byHint = providerNameHints.find((entry) => normalized.includes(entry.hint));
  return byHint?.key;
};

const SetupIntegrationDialog: React.FC<{
  integrationId: string,
  onDialogClosed: CloseDialogHandler
}> = ({ integrationId, onDialogClosed }) => {
  const isSmallScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const {mutateAsync: updateIntegration} = useUpdateIntegration();
  const {data: integration} = useIntegration(integrationId);
  const { data: alertProviders = [] } = useAlertProviders();
  const [err, setError] = React.useState<string>();
  const isSystemIntegration = integration?.system === true;

  const onSubmit = async (integration: Partial<Integration>) => {
    const auth = integration.auth;

    if (
      auth?.type === "oauth" &&
      auth.ready === false &&
      auth.accessToken === "" &&
      auth.refreshToken === "" &&
      auth.expiresAt === 0
    ) {
      onDialogClosed({
        message: "OAuth setup started.",
        severity: "success",
      });
      return;
    }

    try {
      await updateIntegration({integrationId, integration});
      onDialogClosed({
          message: "Integration created!",
          severity: "success"
      });
    }catch(e) {
      console.error(e);
      setError("Something went wrong during integration setup");
    }
  };

  const onClose = () => {
    onDialogClosed();
  };

  const AuthForm = React.useMemo(() => {
    if (!integration) {
      return undefined;
    }

    const formByProvider = integrationSetupForm[
      integration.alertSchemaId as keyof typeof integrationSetupForm
    ];
    if (formByProvider) {
      return formByProvider;
    }

    const matchedProvider = alertProviders.find(
      (provider) => provider.id === integration.alertSchemaId,
    );
    const providerName = matchedProvider?.name ?? integration.name;
    const isSystemIntegrationFlow =
      integration.system === true || matchedProvider?.system === true;
    const resolvedSystemSchema =
      resolveSystemSchemaByText(integration.alertSchemaId) ??
      resolveSystemSchemaByText(providerName);

    if (isSystemIntegrationFlow && resolvedSystemSchema) {
      return integrationSetupForm[resolvedSystemSchema];
    }

    if (isSystemIntegrationFlow) {
      return ((data) => (
        <ApiKeyForm
          id={data.integration.alertSchemaId}
          integration={data.integration}
          onSubmit={data.onSubmit}
        />
      )) as React.FC<{
        integration: Integration;
        onSubmit: (updatedIntegration: Integration) => void;
      }>;
    }

    return integrationSetupForm.custom;
  }, [alertProviders, integration]);

  return (
    <Dialog
      open={true}
      onClose={onClose}
      fullWidth
      fullScreen={isSmallScreen}
      PaperProps={{ sx: { maxWidth: isSmallScreen ? undefined : DIALOG_WIDTH.lg } }}
    >
      <StandardDialogTitle title={"Integration setup"} isSmallScreen={isSmallScreen} onClose={onClose} />

      <DialogContent sx={{ overflowX: "hidden" }}>
        {isSystemIntegration && (
          <FormHelperText sx={{ fontSize: 13, m: 0 }}>
            System integration core fields are read-only. Setup credentials can be configured here.
          </FormHelperText>
        )}
        {AuthForm && integration && 
          <AuthForm 
            onSubmit={onSubmit}
            integration={integration} />
        }
        {
          err
        }
      </DialogContent>
    </Dialog>
  );
};

export default SetupIntegrationDialog;
